/**
 * Carbon Accounting — admin API client.
 *
 * Talks to the admin-only /api/carbon-accounting endpoints. Geocoding and
 * map components live in shared features (features/geocoding, features/maps)
 * because they're reused outside this admin tool.
 */

import { authApi } from '../../auth/api';
import type { LatLng } from '../../../entities/geo/types';

// Re-export for convenience so existing carbonAccounting consumers don't
// have to import LatLng from two different places.
export type { LatLng };

export type TransportMode =
  | 'train'
  | 'bus'
  | 'car'
  | 'airplane'
  | 'motorcycle'
  | 'van'
  | 'ferry';

export interface CarbonAccountingInput {
  from: LatLng;
  to: LatLng;
  mode: TransportMode;
  options?: Record<string, unknown>;
}

export interface EmissionFactor {
  value: number;
  unit: string;
  source: string;
  flightCategory?: 'short' | 'medium' | 'long';
}

export interface SimplifiedResult {
  distanceKm: number;
  co2eKg: number;
  explanation: string;
}

export interface ComprehensiveResult {
  distanceKm: number;
  detourMultiplier: number;
  rfMultiplier?: number;
  co2eKg: number;
  explanation: string;
}

export interface EmissionsResult {
  mode: TransportMode;
  appliedOptions: Record<string, unknown>;
  factor: EmissionFactor;
  simplified: SimplifiedResult;
  comprehensive: ComprehensiveResult;
}

export const calculateCarbonEmissions = async (
  input: CarbonAccountingInput
): Promise<EmissionsResult> => {
  const response = await authApi.post('/carbon-accounting/calculate', input);
  return response.data;
};
